<script setup lang="ts">
  import { ref, computed } from "vue";
  import { useI18n } from "vue-i18n";
  import { Text } from "@ui-kit";

  const { zones = [] } = defineProps<{ zones: { id: string; path: string; location: string }[] }>();

  const { t, locale } = useI18n();

  const carouselRef = ref<HTMLElement | null>(null);
  const currentZoneIndex = ref(0);
  const totalZoneLabel = computed(() => {
    const zoneLocale = {
      one: t("зона"),
      few: t("зоны"),
      many: t("зон"),
      other: t("зон"),
    };
    const pluralRules = new Intl.PluralRules(locale.value);

    return zoneLocale[pluralRules.select(zones.length || 0)];
  });

  const scrollToIndex = (index: number) => {
    if (!carouselRef.value || index === currentZoneIndex.value) return;

    const childElement = carouselRef.value.children[index];

    if (childElement) {
      childElement.scrollIntoView({
        behavior: "smooth",
        block: "nearest",
        inline: "center",
      });
    }

    currentZoneIndex.value = index;
  };

  const handleScrollEnd = () => {
    if (!carouselRef.value) return;

    const children_first = carouselRef.value.children[0];

    const scrollLeft = carouselRef.value.scrollLeft;
    const newIndex = Math.round(scrollLeft / children_first.clientWidth);
    currentZoneIndex.value = newIndex;
  };
</script>
<template>
  <div v-if="zones.length > 0">
    <div ref="carouselRef" class="item-zone-carousel" @scrollend="handleScrollEnd">
      <div
        v-for="(zone, index) in zones"
        :key="zone.id"
        class="item-zone-carousel__zone"
        @click="scrollToIndex(index)"
        data-testid="item-zone-carousel"
      >
        <div class="item-zone-carousel__row">
          <Text font="heading-H5" data-testid="item-zone-path">
            {{ zone.path }}
          </Text>
        </div>
        <div class="item-zone-carousel__row">
          <Text font="heading-H5" data-testid="item-zone-location">
            {{ zone.location }}
          </Text>
        </div>
      </div>
    </div>
    <div v-if="zones.length > 1" class="item-zone-carousel__navigation" data-testid="item-zone-navigation">
      <Text font="body-regular-12" color="tertiary" data-testid="item-zone-total">
        {{ zones.length }} {{ totalZoneLabel }}
      </Text>
      <div class="item-zone-carousel__dots">
        <span
          v-for="(_, index) in zones"
          :key="index"
          class="item-zone-carousel__dot"
          :class="{ 'item-zone-carousel__dot--active': index === currentZoneIndex }"
          data-testid="item-zone-dot"
        />
      </div>
    </div>
  </div>
  <div v-else class="item-zone-carousel__not-found" data-testid="item-zone-not-found">
    <Text color="secondary" font="body-regular-14">{{ $t("Товар не привязан к зоне") }}</Text>
  </div>
</template>

<style scoped>
  .item-zone-carousel {
    display: flex;
    overflow-x: auto;
    scroll-snap-type: x mandatory;
    -webkit-overflow-scrolling: touch;
    scroll-behavior: smooth;
    gap: 8px;

    &::-webkit-scrollbar {
      display: none;
    }
  }

  .item-zone-carousel__zone {
    flex: 0 0 80%;
    display: flex;
    flex-direction: column;
    background-color: var(--sf-base-background-secondary);
    border-radius: 12px;
    scroll-snap-align: center;
    cursor: pointer;
    transition: transform 0.2s;

    &:only-child {
      flex: 1;
    }
  }

  .item-zone-carousel__zone--active {
    transform: scale(1.02);
  }

  .item-zone-carousel__navigation {
    display: flex;
    justify-content: space-between;
    align-items: center;
    gap: 4px;
    margin-top: 2px;
  }

  .item-zone-carousel__dots {
    display: flex;
    gap: 8px;
  }

  .item-zone-carousel__dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    opacity: 0.4;
    background-color: var(--sf-base-border-dark);
  }

  .item-zone-carousel__dot--active {
    opacity: 1;
    background-color: var(--sf-base-border-dark);
  }

  .item-zone-carousel__row {
    display: flex;
    justify-content: center;
    padding: 17px 0px;

    &:not(:first-child) {
      border-top: 1px solid var(--sf-base-border-primary);
    }
  }

  .item-zone-carousel__not-found {
    height: 88px;
    display: flex;
    align-items: center;
    justify-content: center;
    border-radius: 12px;
    background-color: var(--sf-base-background-secondary);
  }
</style>
